import { useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createQuery,
  saveQuery,
  listQueries,
  listMyQueries,
  listMyActionbaleQueries,
  viewQuery,
  modifyQuery,
  modifyQueryAction,
  deleteQuery,
  submitQuery,
  actionQuery,
  actionQueryAction,
  recallQuery,
  escalateQuery,
  escalateQueryAction,
  revertQuery,
  reassignQuery,
  reassignQueryAction,
  reopenQuery,
  reopenQueryAction,
  searchQueryAction,
} from 'api/queries';
import useToast from 'components/Provider/useToast';
import { get } from 'lodash';
import downloadFile from 'utils/downloadFile';

const initialState = {
  open: false,
  selectedTab: 0,
  listQueries: {
    loading: false,
    data: {},
    error: null,
  },
  queryDetails: {},
  isApprove: false,
  comment: '',
};

const formatQueryDetails = (response) => {
  if (!response.data) {
    return {};
  }
  const { queryInstance } = response.data;
  return {
    reportDetails: {
      queryNumber: queryInstance.queryNumber,
      title: queryInstance.title,
      resolutionStatus: queryInstance.resolutionStatus,
      openedBy: queryInstance.openedBy?.fullName,
      closedBy: queryInstance.closedBy?.fullName,
      openDate: queryInstance.openDate,
      closeDate: queryInstance.closeDate,
      submissionDate: queryInstance.submissionDate,
      currentOwner: queryInstance.currentOwner?.fullName,
      category: queryInstance.category?.name,
      topic: queryInstance.topic?.name,
      description: '',
      attachments: queryInstance?.attachment,
    },
  };
};

const formatPreCreateResponse = (response) => {
  if (!response.data) {
    return {};
  }
  const { topicsMap = {}, queryInstance, employeesList = [], categoriesList = [] } = response.data;
  const retObj = {
    topicOptions: Object.keys(topicsMap)?.map((item) => ({
      id: topicsMap[item].id,
      name: topicsMap[item].name,
      onClick: () => {},
    })),
    categoryOptions: categoriesList?.map((item) => ({
      id: item.id,
      name: item.name,
      onClick: () => {},
    })),
    currentOwnerOptions: employeesList?.map((item) => ({
      id: item.id,
      name: item.fullName,
      onClick: () => {},
    })),
    resolutionStatusOptions: [
      { id: 'Draft', name: 'Draft' },
      { id: 'Under Review', name: 'Under Review' },
      { id: 'Closed - Resolved', name: 'Closed - Resolved' },
      { id: 'Closed - Unresolved', name: 'Closed - Unresolved' },
      { id: 'Closed - Duplicate', name: 'Closed - Duplicate' },
      { id: 'Closed - Rejected', name: 'Closed - Rejected' },
      { id: 'Closed - Incorrect', name: 'Closed - Incorrect' },
      { id: 'Unknown', name: 'Unknown' },
    ],
    formData: {
      id: queryInstance?.id,
      title: queryInstance?.title,
      category: queryInstance?.categoryId,
      topic: queryInstance?.topicId,
      description: queryInstance?.description,
      attachment: queryInstance?.attachment?.documentName,
      currentOwner: queryInstance?.currentOwnerId,
      resolutionStatus: queryInstance.resolutionStatus,
    },
    queryDetails: {
      queryNumber: queryInstance?.queryNumber,
      title: queryInstance?.title,
      resolutionStatus: queryInstance?.resolutionStatus,
      openedBy: queryInstance?.openedBy?.fullName,
      closedBy: queryInstance?.closedBy?.fullName,
      openDate: queryInstance?.openDate,
      closeDate: queryInstance?.closeDate,
      submissionDate: queryInstance?.submissionDate,
      currentOwner: queryInstance?.currentOwner?.fullName,
      category: queryInstance?.category?.name,
      topic: queryInstance?.topic?.name,
      description: queryInstance?.description,
      attachments: queryInstance?.attachment?.documentName,
    },
  };

  return retObj;
};

const reducer = (state, action) => {
  const { type, payload } = action;
  let newState;
  switch (type) {
    case 'FETCH_QUERY_PENDING': {
      newState = { ...state, listQueries: { loading: true } };
      break;
    }
    case 'FETCH_QUERY': {
      newState = { ...state, listQueries: { ...state.listQueries, loading: false, data: payload } };
      break;
    }
    case 'FETCH_QUERY_REJECT': {
      newState = { ...state, listQueries: { ...state.listQueries, loading: false, error: payload } };
      break;
    }
    case 'OPEN_CLOSE_DIALOG': {
      newState = { ...state, open: payload };
      break;
    }
    case 'SET_COMMENT': {
      newState = { ...state, comment: payload };
      break;
    }
    case 'SET_QUERY_DETAILS': {
      newState = { ...state, queryDetails: payload };
      break;
    }
    default: {
      newState = state;
    }
  }
  return newState;
};

const useServices = () => {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const addToast = useToast();

  const createQueryNavigationService = () => {
    navigate(`/home/actionable-queries/add`);
  };

  const handleDeleteDialog = () => {
    setShowDeleteDialog(!showDeleteDialog);
  };

  const viewQueryService = (row) => {
    navigate(`/home/actionable-queries/${row.id}/view`);
  };

  const createQueryService = async () => {
    try {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: true } });
      const response = await createQuery();
      if (response.status === 200 && response.data) {
        const formattedResponse = formatPreCreateResponse(response.data);
        dispatch({ type: 'SET_QUERY_DETAILS', payload: formattedResponse });
      }
    } catch (error) {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: false } });
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to create Task/Query`,
      });
    }
  };
  const modifyQueryActionService = async (formik) => {
    try {
      const bodyFormData = new FormData();
      bodyFormData.append('id', formik.id);
      bodyFormData.append('title', formik.title);
      bodyFormData.append('category.id', formik.category);
      bodyFormData.append('topic.id', formik.topic);
      bodyFormData.append('description', formik.description);
      bodyFormData.append('attachment', formik.attachment);
      bodyFormData.append('topicidtemp', 30546);
      bodyFormData.append(`topic.id_${formik.category}`, formik.topic);
      const response = await modifyQueryAction(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query updated successfully`,
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to update Task/Query`,
      });
    }
  };
  const saveQueryService = async (isEditMode, formData) => {
    let response;
    const bodyFormData = new FormData();
    try {
      bodyFormData.append('title', get(formData, 'title', ''));
      bodyFormData.append('category.id', get(formData, 'category', ''));
      bodyFormData.append('topic.id', get(formData, 'topic', ''));
      bodyFormData.append('description', get(formData, 'description', ''));
      bodyFormData.append('attachment', get(formData, 'attachment', ''));
      bodyFormData.append('topicidtemp', 30546);
      bodyFormData.append(`topic.id_${formData.category}`, get(formData, 'topic', ''));
      if (isEditMode) {
        bodyFormData.append('id', get(formData, 'id', ''));
        response = await modifyQueryAction(bodyFormData);
      } else {
        response = await saveQuery(bodyFormData);
      }
      if (response.status === 200 && response.data) {
        const queryId = isEditMode ? formData?.id : response.data?.queryInstance?.id;
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Query saved successfully`,
        });
        viewQueryService({ id: queryId });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to save Query`,
      });
    }
  };
  const listQueriesService = async (query) => {
    try {
      dispatch({ type: 'FETCH_QUERY_PENDING', payload: null });
      const response = await listQueries(query);
      if (response.status === 200 && response.data) {
        dispatch({ type: 'FETCH_QUERY', payload: response.data });
      }
    } catch (error) {
      dispatch({ type: 'FETCH_QUERY_REJECT', payload: error });
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to list Task/Query`,
      });
    }
  };
  const listMyQueriesService = async (query) => {
    try {
      dispatch({ type: 'FETCH_QUERY_PENDING', payload: null });
      const response = await listMyQueries(query);
      if (response.status === 200 && response.data) {
        dispatch({ type: 'FETCH_QUERY', payload: response.data });
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query listed successfully`,
        });
      }
    } catch (error) {
      dispatch({ type: 'FETCH_QUERY_REJECT', payload: error });
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to list Task/Query`,
      });
    }
  };
  const listMyActionbaleQueriesService = async (query) => {
    try {
      dispatch({ type: 'FETCH_QUERY_PENDING', payload: null });
      const response = await listMyActionbaleQueries(query);
      if (response.status === 200 && response.data) {
        dispatch({ type: 'FETCH_QUERY', payload: response.data });
      }
    } catch (error) {
      dispatch({ type: 'FETCH_QUERY_REJECT', payload: error });
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to list Task/Query`,
      });
    }
  };

  const viewQueryDetailsService = async (id) => {
    try {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: true } });
      const bodyFormData = new FormData();
      bodyFormData.append('id', id);
      const response = await viewQuery(bodyFormData);
      if (response.status === 200 && response.data) {
        const formattedResponse = formatQueryDetails(response.data);
        dispatch({ type: 'SET_QUERY_DETAILS', payload: formattedResponse });
      }
    } catch (error) {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: false } });
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to view Task/Query`,
      });
    }
  };
  const modifyQueryNavigationService = (row) => {
    navigate(`/home/actionable-queries/${row.id}/edit`);
  };
  const modifyQueryService = async (id) => {
    try {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: true } });
      const bodyFormData = new FormData();
      bodyFormData.append('id', id);
      const response = await modifyQuery(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query updated successfully`,
        });
        const formattedResponse = formatPreCreateResponse(response.data);
        dispatch({ type: 'SET_QUERY_DETAILS', payload: formattedResponse });
      }
    } catch (error) {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: false } });
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to update Task/Query`,
      });
    }
  };

  const handleOpenDialog = (payload, row) => {
    dispatch({ type: 'OPEN_CLOSE_DIALOG', payload });
    dispatch({ type: 'SET_QUERY_DETAILS', payload: row });
  };

  const deleteQueryService = async (id) => {
    try {
      const bodyFormData = new FormData();
      bodyFormData.append('id', id);
      const response = await deleteQuery(bodyFormData);
      if (response.status === 200 && response.data) {
        handleOpenDialog(false);
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Query deleted successfully`,
        });
      }
    } catch (error) {
      handleOpenDialog(false);
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to delete the query`,
      });
    } finally {
      handleOpenDialog(false);
    }
  };

  const submitQueryService = async () => {
    try {
      const bodyFormData = new FormData();
      const response = await submitQuery(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query submitted successfully`,
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to submit Task/Query`,
      });
    }
  };
  const actionQueryNavigationService = async (row) => {
    navigate(`/home/actionable-queries/${row.id}/action`);
  };
  const actionQueryService = async (id) => {
    try {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: true } });
      const bodyFormData = new FormData();
      bodyFormData.append('id', id);
      const response = await actionQuery(bodyFormData);
      if (response.status === 200 && response.data) {
        const formattedResponse = formatPreCreateResponse(response.data);
        dispatch({ type: 'SET_QUERY_DETAILS', payload: formattedResponse });
      }
    } catch (error) {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: false } });
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to fetch Task/Query`,
      });
    }
  };
  const actionQueryActionService = async (formik) => {
    try {
      const bodyFormData = new FormData();
      bodyFormData.append('id', formik.id);
      bodyFormData.append('resolutionStatus', formik.resolutionStatus);
      bodyFormData.append('comments', formik.comments);
      const response = await actionQueryAction(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query fetched successfully`,
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to fetch Task/Query`,
      });
    }
  };
  const recallQueryService = async () => {
    try {
      const bodyFormData = new FormData();
      const response = await recallQuery(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query recalled successfully`,
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to recall Task/Query`,
      });
    }
  };

  const escalateQueryNavigationService = (row) => {
    navigate(`/home/actionable-queries/${row.id}/escalate`);
  };
  const escalateQueryService = async (id) => {
    try {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: true } });
      const bodyFormData = new FormData();
      bodyFormData.append('id', id);
      const response = await escalateQuery(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query escalated successfully`,
        });
        const formattedResponse = formatQueryDetails(response.data);
        dispatch({ type: 'SET_QUERY_DETAILS', payload: formattedResponse });
      }
    } catch (error) {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: false } });
      throw error;
    }
  };
  const escalateQueryActionService = async () => {
    try {
      const bodyFormData = new FormData();
      const response = await escalateQueryAction(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query escalated successfully`,
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to escalate Task/Query`,
      });
    }
  };
  const revertQueryService = async (row) => {
    try {
      const bodyFormData = new FormData();
      bodyFormData.append('id', get(row, 'id', ''));
      const response = await revertQuery(bodyFormData);
      if (response.status === 200 && response.data) {
        navigate(`/home/actionable-queries/${row?.id}/view`);
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query reverted successfully`,
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to revert Task/Query`,
      });
    }
  };
  const reassignQueryNavigationService = (row) => {
    navigate(`/home/actionable-queries/${row.id}/reassign`);
  };

  const reassignQueryService = async (id) => {
    try {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: true } });
      const bodyFormData = new FormData();
      bodyFormData.append('id', id);
      const response = await reassignQuery(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query reassigned successfully`,
        });
        const formattedResponse = formatPreCreateResponse(response.data);
        dispatch({ type: 'SET_QUERY_DETAILS', payload: formattedResponse });
      }
    } catch (error) {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: false } });
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to reassign Task/Query`,
      });
    }
  };
  const reassignQueryActionService = async (formik) => {
    try {
      const bodyFormData = new FormData();
      bodyFormData.append('id', formik.id);
      bodyFormData.append('category.id', formik.category);
      bodyFormData.append('topic.id', formik.topic);
      bodyFormData.append('topicidtemp', 30546);
      bodyFormData.append(`topic.id_${formik.category}`, formik.topic);
      const response = await reassignQueryAction(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query reassigned successfully`,
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to reassign Task/Query`,
      });
    }
  };
  const handleReassignCancel = () => {
    navigate(`/home/actionable-queries`);
  };
  const reopenQueryNavigationService = (row) => {
    navigate(`/home/actionable-queries/${row.id}/reopen`);
  };
  const reopenQueryService = async (id) => {
    try {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: true } });
      const bodyFormData = new FormData();
      bodyFormData.append('id', id);
      const response = await reopenQuery(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query reopened successfully`,
        });
        const formattedResponse = formatPreCreateResponse(response.data);
        dispatch({ type: 'SET_QUERY_DETAILS', payload: formattedResponse });
      }
    } catch (error) {
      dispatch({ type: 'SET_QUERY_DETAILS', payload: { isLoading: false } });
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to reopen Task/Query`,
      });
    }
  };
  const reopenQueryActionService = async () => {
    try {
      const bodyFormData = new FormData();
      const response = await reopenQueryAction(bodyFormData);
      if (response.status === 200 && response.data) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || `Task/Query reopened successfully`,
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to reopen Task/Query`,
      });
    }
  };

  const onChangeComment = (event) => {
    dispatch({ type: 'SET_COMMENT', payload: event.target.value });
  };

  const downloadDocument = async (id, filename) => {
    try {
      await downloadFile({ docid: id }, filename, 'restApi/downloadDocumentAttachment');
    } catch (error) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to download document`,
      });
    }
  };

  const searchQueriesService = async (query) => {
    try {
      dispatch({ type: 'FETCH_QUERY_PENDING', payload: { loading: true } });
      const response = await searchQueryAction(query);
      dispatch({ type: 'FETCH_QUERY_PENDING', payload: { loading: false } });
      if (response.status === 200 && response.data) {
        dispatch({ type: 'FETCH_QUERY', payload: response.data });
      }
    } catch (error) {
      dispatch({ type: 'FETCH_QUERY_REJECT', payload: error });
      addToast({
        type: 'error',
        title: 'ERROR',
        message: error?.message || `Failed to list Task/Query`,
      });
    }
  };

  return {
    state,
    createQueryNavigationService,
    createQueryService,
    saveQueryService,
    listQueriesService,
    listMyQueriesService,
    listMyActionbaleQueriesService,
    viewQueryService,
    viewQueryDetailsService,
    modifyQueryNavigationService,
    modifyQueryService,
    modifyQueryActionService,
    deleteQueryService,
    submitQueryService,
    actionQueryNavigationService,
    actionQueryService,
    actionQueryActionService,
    recallQueryService,
    escalateQueryNavigationService,
    escalateQueryService,
    escalateQueryActionService,
    revertQueryService,
    reassignQueryNavigationService,
    reassignQueryService,
    reassignQueryActionService,
    handleReassignCancel,
    reopenQueryNavigationService,
    reopenQueryService,
    reopenQueryActionService,
    downloadDocument,
    handleOpenDialog,
    onChangeComment,
    searchQueriesService,
    showDeleteDialog,
    handleDeleteDialog,
  };
};

export default useServices;
